import { useEffect, useState, useCallback } from 'react';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export default function AdminInvitedPanel({ user }) {
  const [invited, setInvited] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadInvited() {
      if (!user) {
        if (isMounted) {
          setInvited([]);
          setLoading(false);
        }
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const resp = await fetch('/api/v1/admin/invited', {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!resp.ok) throw new Error(`Failed to fetch invited list: ${resp.status}`);
        const data = await resp.json();
        if (isMounted) {
          setInvited(data.invited || []);
          setError(null);
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadInvited();
    return () => { isMounted = false; };
  }, [user, refreshTrigger]);

  const sortedInvited = [...invited].sort((a, b) => {
    const aTime = new Date(a.sinkRequestedAt || a.probRequestedAt || 0).getTime();
    const bTime = new Date(b.sinkRequestedAt || b.probRequestedAt || 0).getTime();
    return bTime - aTime;
  });

  if (loading && invited.length === 0) {
    return <div className="p-4 text-gray-400">Loading invited list...</div>;
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
      <div className="p-3 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
        <div>
          <div className="text-xs font-bold text-white">招待リスト</div>
          <div className="text-[10px] text-gray-500">Sink / Prob から登録された invited ドキュメント</div>
        </div>
        <button onClick={handleRefresh} disabled={loading} className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
          {loading ? '...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="p-3 bg-rose-900/20 text-rose-400 text-xs border-b border-rose-900/30">Error: {error}</div>}

      <div className="flex-1 overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-gray-800 text-gray-400 sticky top-0">
            <tr>
              <th className="p-3 border-b border-gray-700">Email / UID</th>
              <th className="p-3 border-b border-gray-700">Name</th>
              <th className="p-3 border-b border-gray-700">Sink</th>
              <th className="p-3 border-b border-gray-700">Prob</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {sortedInvited.map((item) => (
              <tr key={item.uid} className="hover:bg-gray-800/40 transition-colors">
                <td className="p-3">
                  <div className="font-medium text-white">{item.email || 'No email'}</div>
                  <div className="text-[10px] text-gray-500 font-mono">{item.uid}</div>
                </td>
                <td className="p-3 text-gray-300">{item.displayName || '—'}</td>
                <td className="p-3">
                  <span className={item.sinkRequestedAt ? 'text-emerald-300' : 'text-gray-600'}>
                    {formatDate(item.sinkRequestedAt)}
                  </span>
                </td>
                <td className="p-3">
                  <span className={item.probRequestedAt ? 'text-emerald-300' : 'text-gray-600'}>
                    {formatDate(item.probRequestedAt)}
                  </span>
                </td>
              </tr>
            ))}
            {sortedInvited.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-gray-500">
                  招待リスト登録はまだありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
